import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import routePaths from "../../router/route-paths";
import { getUserAntiCheatProfile } from "../../http/antiCheat";

const sectionSx = {
  padding: "15px",
  marginBottom: "20px",
  borderRadius: "6px",
  boxShadow: "rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px",
};

const titleSx = {
  fontWeight: 600,
  fontSize: { xs: "14px", sm: "15px", md: "16px" },
  marginBottom: "12px",
  color: "#142C5C",
};

const headCellSx = {
  fontWeight: 600,
  color: "#000",
  backgroundColor: "#e0e0e0",
  fontSize: "13px",
  whiteSpace: "nowrap",
};

const cellSx = { fontSize: "13px", color: "#333" };

const shortDevice = (deviceId) =>
  deviceId && deviceId.length > 14
    ? `${deviceId.slice(0, 8)}…${deviceId.slice(-4)}`
    : deviceId || "—";

const fmt = (value) => (value ? dayjs(value).format("MMM D, YYYY h:mm A") : "—");

const Stat = ({ label, value, warn }) => (
  <Box>
    <Typography sx={{ fontSize: "12px", color: "#666" }}>{label}</Typography>
    <Typography sx={{ fontSize: "14px", fontWeight: 600, color: warn ? "#c62828" : "#333" }}>
      {value}
    </Typography>
  </Box>
);

/**
 * "Devices & linked accounts" for View User: every install this account has
 * been seen on, the other accounts on those installs, and how many of this
 * user's received votes came from them. Powered by
 * GET /admin/anti-cheat/user/:id.
 */
const UserDevicesSection = ({ userId }) => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const response = await getUserAntiCheatProfile(userId);
        if (!cancelled) {
          setProfile(response?.data || null);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.message || err?.message || "Failed to load devices");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const devices = profile?.devices || [];
  const linked = profile?.linkedAccounts || [];
  const votes = profile?.votes || {};
  const blocked = profile?.blockedVotes || [];
  const flagged = Boolean(profile?.report?.flagged);

  return (
    <Box sx={sectionSx}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
        <Typography sx={titleSx}>Devices &amp; Linked Accounts</Typography>
        {flagged && <Chip size="small" color="error" label="Flagged by nightly scan" />}
      </Box>

      {error && (
        <Typography color="error" sx={{ fontSize: "13px", mb: 1 }}>
          {error}
        </Typography>
      )}

      {!loading && !error && (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(5, 1fr)" },
              gap: "12px",
              mb: 2,
            }}
          >
            <Stat label="Installs seen on" value={devices.length} />
            <Stat label="Linked accounts" value={linked.length} warn={linked.length > 0} />
            <Stat
              label="Votes from linked accounts"
              value={`${votes.linkedReceived ?? 0} / ${votes.totalReceived ?? 0}`}
              warn={(votes.linkedReceived ?? 0) > 0}
            />
            <Stat
              label={`Share (last ${votes.windowDays ?? 30}d)`}
              value={
                votes.totalReceivedInWindow
                  ? `${Math.round(((votes.linkedReceivedInWindow ?? 0) / votes.totalReceivedInWindow) * 100)}%`
                  : "—"
              }
              warn={(votes.linkedReceivedInWindow ?? 0) > 0}
            />
            <Stat label="Blocked vote attempts" value={blocked.length} warn={blocked.length > 0} />
          </Box>

          {devices.length === 0 ? (
            <Typography sx={{ fontSize: "13px", color: "#777" }}>
              No install has been recorded for this account yet (older account, or the app never sent a device id).
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
              {devices.map((d) => (
                <Tooltip
                  key={d.deviceId}
                  title={`${d.deviceId}\nfirst ${fmt(d.firstSeenAt)} · last ${fmt(d.lastSeenAt)} · ${d.seenCount} sightings (${d.source})`}
                >
                  <Chip
                    size="small"
                    variant={d.isRegistrationDevice ? "filled" : "outlined"}
                    label={`${shortDevice(d.deviceId)}${d.platform ? ` · ${d.platform}` : ""}${
                      d.isRegistrationDevice ? " · signup" : ""
                    }`}
                    sx={{ fontFamily: "monospace", fontSize: "11px" }}
                  />
                </Tooltip>
              ))}
            </Box>
          )}

          {linked.length > 0 && (
            <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e0e0e0" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Linked account</TableCell>
                    <TableCell sx={headCellSx}>Shared installs</TableCell>
                    <TableCell sx={headCellSx}>Votes for this user</TableCell>
                    <TableCell sx={headCellSx}>Votes this user cast for them</TableCell>
                    <TableCell sx={headCellSx}>Shared IPs (90d)</TableCell>
                    <TableCell sx={headCellSx}>Last seen together</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {linked.map((row) => {
                    const u = row.user || {};
                    return (
                      <TableRow key={String(u._id)} hover>
                        <TableCell sx={cellSx}>
                          {u.username ? (
                            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                              <Link
                                component="button"
                                underline="hover"
                                sx={{ fontSize: "13px", fontWeight: 600 }}
                                onClick={() => navigate(routePaths.VIEW_USER.replace(":id", u._id))}
                              >
                                @{u.username}
                              </Link>
                              <Link
                                component="button"
                                underline="hover"
                                sx={{ fontSize: "11px" }}
                                onClick={() =>
                                  navigate(
                                    `${routePaths.USER_MODIFICATION}?username=${encodeURIComponent(u.username)}`
                                  )
                                }
                              >
                                Account Tools
                              </Link>
                              {u.isDeleted && <Chip size="small" label="Deleted" />}
                              {u.isAccountBlocked && <Chip size="small" color="error" label="Blocked" />}
                            </Box>
                          ) : (
                            <Typography sx={{ fontSize: "12px", color: "#999" }}>
                              (deleted {String(u._id).slice(-6)})
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={cellSx}>
                          <Tooltip title={(row.sharedDeviceIds || []).join("\n")}>
                            <span>{(row.sharedDeviceIds || []).length}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ ...cellSx, fontWeight: row.votesReceivedFrom > 0 ? 700 : 400 }}>
                          {row.votesReceivedFrom}
                          {row.lastVoteReceivedAt && (
                            <Typography sx={{ fontSize: "11px", color: "#888" }}>
                              last {fmt(row.lastVoteReceivedAt)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={cellSx}>{row.votesCastFor}</TableCell>
                        <TableCell sx={cellSx}>{row.sharedIpCount}</TableCell>
                        <TableCell sx={cellSx}>{fmt(row.lastSeenAt)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
};

export default UserDevicesSection;
